import React, { useState, useEffect, useRef, useMemo } from "react";
import { Chart, registerables } from "chart.js";
import { Form, Select, Button, Table, Pagination } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { getStates } from "../features/statecity/statecitySlice";
import { getVacantList } from "../features/vacantposition/vacantSlice";
import { getvacantreportlist } from "../features/dashboard/dashboardSlice";
import { ExportOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";

Chart.register(...registerables);
const { Option } = Select;

const columns = [
  { title: "S.No.", dataIndex: "key", key: "key", width: 10 },
  { title: "State", dataIndex: "state", key: "state" },
  { title: "District", dataIndex: "district", key: "district" },
  {
    title: "Vacant Position",
    dataIndex: "vacantposition",
    key: "vacantposition",
  },
  {
    title: "UDISE Code",
    dataIndex: "school_udise_code",
    key: "school_udise_code",
  },
  { title: "School Name", dataIndex: "schoolname", key: "schoolname" },
  { title: "Trade Name", dataIndex: "trades", key: "trades" },
];

const VacantPosition = () => {
  const dispatch = useDispatch();
  const [vacantList, setVacantList] = useState([]);
  const [filteredVacantList, setFilteredVacantList] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const doughnutRef = useRef(null);
  const chartInstance = useRef(null);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getStates());
    dispatch(getvacantreportlist());
    dispatch(getVacantList());
  }, [dispatch]);

  const statelistState = useSelector((state) => state.statecity.statelist);
  const vacantlistState = useSelector((state) => state.vacantList.vacantLists);

  const processedVacantList = useMemo(() => {
    if (
      vacantlistState &&
      typeof vacantlistState === "object" &&
      Object.keys(vacantlistState).length > 0
    ) {
      return Object.entries(vacantlistState).flatMap(([state, data]) =>
        data.schools.map((school, index) => ({
          key: `${index + 1}`,
          state: school.state || "Unknown",
          district: school.district || "Unknown",
          school_udise_code: school.UDISE_Code || "N/A",
          schoolname: school.school_name || "Unknown",
          trades: school.trades?.join(", ") || "N/A",
          vacantposition:
            school.vacantposition !== undefined ? school.vacantposition : 1,
        }))
      );
    }
    console.log("No valid vacant list data available");
    return [];
  }, [vacantlistState]);

  useEffect(() => {
    setVacantList(processedVacantList);
    setFilteredVacantList(processedVacantList);
  }, [processedVacantList]);

  // Update graph data based on filtered table data for all states
  useEffect(() => {
    if (!doughnutRef.current) {
      console.log("Canvas reference not available");
      return;
    }

    const totalWorking = filteredVacantList.filter(
      (item) => item.vacantposition === 0 || item.vacantposition === false
    ).length;
    const totalVacant = filteredVacantList.filter(
      (item) => item.vacantposition === 1 || item.vacantposition === true
    ).length;

    const newVacantData = [
      { name: "Working", value: totalWorking },
      { name: "Vacant", value: totalVacant },
    ];

    if (totalWorking === 0 && totalVacant === 0) {
      console.log("No data to render in the chart");
      return;
    }

    const doughnutCtx = doughnutRef.current.getContext("2d");

    if (!doughnutCtx) {
      console.log("Failed to get 2D context from canvas");
      return;
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    try {
      chartInstance.current = new Chart(doughnutCtx, {
        type: "doughnut",
        data: {
          labels: ["Working Positions", "Vacant Positions"],
          datasets: [
            {
              data: newVacantData.map((item) => item.value),
              backgroundColor: ["#4caf50", "#f44336"],
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "top",
            },
            title: {
              display: true,
              text: "Total Vacant and Working Positions Across States",
            },
          },
        },
      });
    } catch (error) {
      console.error("Error creating Chart.js instance:", error);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [filteredVacantList]);

  const handleStateChange = (value) => {
    form.setFieldsValue({ district_name: undefined });
    setFilters((prev) => ({ ...prev, state_name: value }));
  };

  const handleSubmit = (values) => {
    let filteredList = [...vacantList];

    if (values.state_name) {
      filteredList = filteredList.filter(
        (vacant) =>
          vacant.state.toLowerCase() === values.state_name.toLowerCase()
      );
    }

    if (values.district_name) {
      filteredList = filteredList.filter(
        (vacant) =>
          vacant.district.toLowerCase() === values.district_name.toLowerCase()
      );
    }

    setFilteredVacantList(filteredList);
    setFilters(values);
    setCurrentPage(1);
  };

  const filteredDistricts = vacantList
    .filter((item) => item.state === filters.state_name)
    .map((item) => item.district)
    .filter((value, index, self) => self.indexOf(value) === index);

  const resetFilters = () => {
    form.resetFields();
    setFilteredVacantList(vacantList);
    setFilters({});
    setCurrentPage(1);
  };

  const csvHeaders = [
    { label: "State", key: "state" },
    { label: "District", key: "district" },
    { label: "Vacant Position", key: "vacantposition" },
    { label: "School UDISE Code", key: "school_udise_code" },
    { label: "School Name", key: "schoolname" },
    { label: "Trade Name", key: "trades" },
  ];

  const paginatedData = filteredVacantList.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <h4
        style={{
          fontSize: "large",
          fontWeight: "bold",
          margin: "0 0 20px 0",
          textAlign: "center",
        }}
      >
        Vacant Position Tracker
      </h4>
      <Form
        form={form}
        layout="inline"
        onFinish={handleSubmit}
        style={{ marginTop: "20px" }}
      >
        <Form.Item name="state_name">
          <Select
            placeholder="Select State"
            style={{ width: 150, marginRight: 5 }}
            onChange={handleStateChange}
          >
            <Option value="">All States</Option>
            {statelistState.map((i, j) => (
              <Option key={j} value={i.state_name}>
                {i.state_name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="district_name">
          <Select
            placeholder="Select District"
            style={{ width: 150, marginRight: 5 }}
            disabled={!filters.state_name}
          >
            <Option value="">All Districts</Option>
            {filteredDistricts.map((district, index) => (
              <Option key={index} value={district}>
                {district}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <div style={{ display: "flex" }}>
          <Form.Item>
            <Button
              style={{
                backgroundColor: "#28a745",
                color: "#fff",
                marginRight: -20,
              }}
              htmlType="submit"
            >
              Apply Filter
            </Button>
          </Form.Item>

          <Form.Item>
            <Button
              style={{
                backgroundColor: "#dc3545",
                color: "#fff",
              }}
              onClick={resetFilters}
            >
              Reset Filter
            </Button>
          </Form.Item>
        </div>
      </Form>

      {filteredVacantList.length > 0 ? (
        <div style={styles.chartContainer}>
          <canvas ref={doughnutRef}></canvas>
        </div>
      ) : (
        <div style={styles.chartContainer}>No data available to display</div>
      )}

      <div
        className="table-container"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "0.5rem",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          padding: "1rem",
          marginTop: "1.5rem",
        }}
      >
        <Table
          className="custom-table"
          columns={columns}
          dataSource={paginatedData}
          pagination={false}
        />
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredVacantList.length}
          onChange={handlePageChange}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          onShowSizeChange={handlePageSizeChange}
          style={{ marginTop: "20px", textAlign: "right" }}
        />
      </div>

      <div style={{ margin: "2px 0 0 20px", display: "flex", gap: "30px" }}>
        <CSVLink
          data={filteredVacantList || []}
          headers={csvHeaders}
          filename={`Vacant-Positions${new Date().toISOString()}.csv`}
        >
          <Button
            type="primary"
            className="exportcsv"
            style={{ marginBottom: 10 }}
          >
            <ExportOutlined /> Export to CSV
          </Button>
        </CSVLink>
      </div>
    </div>
  );
};

const styles = {
  chartContainer: {
    marginBottom: "20px",
    marginTop: "20px",
    padding: "10px",
    width: "100%",
    height: "300px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    backgroundColor: "#DCDCDC",
    borderRadius: "8px",
    display: "block",
    overflow: "hidden",
  },
};

export default VacantPosition;
